import React from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { DollarSign, TrendingUp, Calculator, PiggyBank, Target, AlertTriangle } from 'lucide-react'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface FinancialImpactProps {
  timeRange: TimeRange
}

const monthlySavingsData = [
  { month: 'Jul', savings: 31170, prevented: 1039, potential: 41000 },
  { month: 'Aug', savings: 42340, prevented: 1411, potential: 58000 },
  { month: 'Sep', savings: 38920, prevented: 1297, potential: 52000 },
  { month: 'Oct', savings: 45670, prevented: 1522, potential: 61000 },
  { month: 'Nov', savings: 52340, prevented: 1745, potential: 69000 },
  { month: 'Dec', savings: 48920, prevented: 1631, potential: 65000 }
]

const costBreakdown = [
  { category: 'System Cost', amount: 105, type: 'expense', description: 'Monthly subscription' },
  { category: 'Staff Time', amount: 2400, type: 'expense', description: 'Review & management' },
  { category: 'False Positives', amount: 1260, type: 'loss', description: 'Lost legitimate leads' },
  { category: 'Fraud Prevented', amount: 31170, type: 'savings', description: 'Blocked fraudulent leads' },
  { category: 'Quality Improvement', amount: 4500, type: 'savings', description: 'Better conversion rates' }
]

const roiMetrics = {
  systemCost: 105,
  fraudPrevented: 31170,
  roi: 29671,
  paybackPeriod: '<1 day',
  netSavings: 27405,
  monthlyROI: 261
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-semibold text-foreground">{label}</p>
        <div className="space-y-1">
          <p className="text-sm text-green-600">
            Savings: ${data.savings?.toLocaleString()}
          </p>
          <p className="text-sm text-blue-600">
            Leads Prevented: {data.prevented?.toLocaleString()}
          </p>
          <p className="text-sm text-gray-600">
            Potential Loss: ${data.potential?.toLocaleString()}
          </p>
        </div>
      </div>
    )
  }
  return null
}

export const FinancialImpact: React.FC<FinancialImpactProps> = ({ timeRange }) => {
  return (
    <div className="space-y-6">
      {/* ROI Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border-green-200 dark:border-green-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly ROI</p>
                <p className="text-2xl font-bold text-green-600">
                  {roiMetrics.roi.toLocaleString()}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
                <Calculator className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">System Cost</p>
                <p className="text-2xl font-bold text-foreground">
                  ${roiMetrics.systemCost}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded">
                <PiggyBank className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Savings</p>
                <p className="text-2xl font-bold text-purple-600">
                  ${roiMetrics.netSavings.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded">
                <Target className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payback Period</p>
                <p className="text-2xl font-bold text-orange-600">
                  {roiMetrics.paybackPeriod}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cumulative Savings Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Cumulative Cost Savings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlySavingsData}>
                <defs>
                  <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="savings"
                  stroke="#10B981"
                  fillOpacity={1}
                  fill="url(#savingsGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Cost Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Cost-Benefit Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {costBreakdown.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded ${
                    item.type === 'savings' ? 'bg-green-100 dark:bg-green-900/30' :
                    item.type === 'loss' ? 'bg-red-100 dark:bg-red-900/30' :
                    'bg-gray-100 dark:bg-gray-900/30'
                  }`}>
                    <DollarSign className={`h-4 w-4 ${
                      item.type === 'savings' ? 'text-green-600' :
                      item.type === 'loss' ? 'text-red-600' :
                      'text-gray-600'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{item.category}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${
                    item.type === 'savings' ? 'text-green-600' :
                    item.type === 'loss' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {item.type === 'expense' ? '-' : item.type === 'savings' ? '+' : '-'}
                    ${item.amount.toLocaleString()}
                  </p>
                  <Badge variant={
                    item.type === 'savings' ? 'default' :
                    item.type === 'loss' ? 'destructive' : 'secondary'
                  } className="text-xs mt-1">
                    {item.type.toUpperCase()}
                  </Badge>
                </div>
              </div>
            ))}
            
            {/* Net Result */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded">
                    <PiggyBank className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900 dark:text-green-100">Net Monthly Impact</p>
                    <p className="text-sm text-green-700 dark:text-green-300">Total savings after all costs</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">
                    +${roiMetrics.netSavings.toLocaleString()}
                  </p>
                  <p className="text-sm text-green-600">
                    {roiMetrics.monthlyROI}x return
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investment Justification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Investment Justification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3">Current Performance</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Monthly System Cost:</span>
                  <span className="font-semibold">$105</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Fraud Prevented:</span>
                  <span className="font-semibold text-green-600">$31,170</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">ROI:</span>
                  <span className="font-bold text-lg text-green-600">29,671%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Break-even Time:</span>
                  <span className="font-semibold text-blue-600">&lt; 1 day</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Scale Potential</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">At 2x Lead Volume:</span>
                  <span className="font-semibold text-green-600">$62,340 saved</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">At 5x Lead Volume:</span>
                  <span className="font-semibold text-green-600">$155,850 saved</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Annual Projection:</span>
                  <span className="font-bold text-lg text-green-600">$374K saved</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">System Upgrade Cost:</span>
                  <span className="font-semibold">$500/month</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Recommendation */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                  Investment Recommendation
                </h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Current ROI of 29,671% strongly justifies system expansion. Consider upgrading to handle 
                  increased lead volume and implementing advanced ML models for even better accuracy.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}